import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
export function HealthNetworks() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'Health'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-2217">
      <Header data-id="element-2218" />
      <main className="pt-[120px]" data-id="element-2219">
        {/* SECTION 1: HERO */}
        <section className="pt-24 pb-28 px-6" data-id="element-2220">
          <div className="max-w-3xl mx-auto" data-id="element-2221">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-2222">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-2223">
                {isFR ? 'SECTEURS · SANTÉ' : 'INDUSTRIES · HEALTH'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-2224">
                {isFR ? 'Identité vérifiée. Consentement. Accès.' : 'Verified identity. Consent. Access.'}
              </h1>
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-10" data-id="element-2225">
                {isFR ? "La santé se précipite vers l'IA. Pourtant, à peine 1 % des organisations jugent leur adoption pleinement mature — la plupart restent bloquées au stade des projets pilotes, freinées par des systèmes fragmentés." : 'Health is rushing into AI. Yet barely 1% of organizations say their adoption is fully mature — most stay stuck in pilots, blocked by fragmented systems.'}
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-2226">
                <Link to="/request-demo?industry=health" onClick={() => track('Demo CTA Clicked', {
                location: 'Health Hero',
                cta: 'Talk to us'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-2227">
                  {isFR ? 'Parlez-nous' : 'Talk to us'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2228" />
                </Link>
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Health Hero',
                cta: 'Request a Demo'
              })} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-2229">
                  {isFR ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2230" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: RESEARCH SHOWS */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2231">
          <div className="max-w-3xl mx-auto" data-id="element-2232">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-2233">
              {isFR ? 'La recherche montre' : 'Research Shows'}
            </motion.div>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-base text-[#121212]/55 leading-relaxed flex items-start gap-3 mb-8" data-id="element-2234">
              <span className="mt-2 inline-block w-1.5 h-1.5 bg-[#121212]/30 shrink-0" aria-hidden="true" data-id="element-2235" />
              <span data-id="element-2236">
                {isFR ? "C'est pourquoi 61 % des organisations de santé qui adoptent l'IA passent par un partenaire spécialisé plutôt que de bâtir seules. " : "That's why 61% of health organizations adopting AI go through a specialized partner rather than building alone. "}
                <a href="https://www.mckinsey.com/industries/healthcare/our-insights" target="_blank" rel="noopener noreferrer" className="text-[#4F46E5] underline underline-offset-2 hover:text-[#4F46E5]/80 transition-colors" data-id="element-2237">
                  {isFR ? 'Voir la recherche' : 'See the research'}
                </a>
                .
              </span>
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="border-t border-[#E4E6EC] pt-6 text-xs text-[#121212]/40 leading-relaxed" data-id="element-2238">
              {isFR ? 'Contexte de marché. Pas des résultats de déploiement BCITI. Source : McKinsey, Technology Trends Outlook 2025.' : 'Market context. Not BCITI deployment results. Source: McKinsey, Technology Trends Outlook 2025.'}
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: BCITI ANSWER */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-2239">
          <div className="max-w-3xl mx-auto" data-id="element-2240">
            <motion.p {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-2241">
              {isFR ? "En santé, l'IA échoue faute de fondation : qui accède à quoi, avec quel consentement. C'est exactement ce que fait notre base d'identité. La confiance se bâtit sur la vérification, pas sur une promesse." : "In health, AI fails for lack of a foundation: who accesses what, with what consent. That's exactly what our identity base does. Trust is built on verification, not on a promise."}
            </motion.p>
          </div>
        </section>
      </main>
      <Footer data-id="element-2242" />
    </div>;
}